/**
 * Разбор текста песни на секции по маркерам вида `[Verse]`, `[Chorus 2]`,
 * `[Pre-Chorus]` и т.п. — чистая функция (без React), тестируемая отдельно.
 *
 * Правила:
 *  - строка целиком в квадратных скобках (`^\[.*\]$`) — это маркер секции;
 *  - для RU известные маркеры локализуются (Verse → Куплет), номер сохраняется
 *    («Verse 2» → «Куплет 2»);
 *  - для EN известные маркеры показываем как есть (без скобок): «Verse 2», «Chorus»;
 *  - НЕизвестные/технические маркеры (`[End]`, `[male vocals]`, `[Guitar Solo]`)
 *    в любом языке СКРЫВАЕМ (заголовок не добавляется) — чтобы не мусорить текст;
 *    опцией `keepUnknownMarkers` их можно оставить как есть (см. ниже);
 *  - остальные строки собираются в текстовые блоки (с сохранением переводов строк).
 */
// Базовые названия секций → RU. Ключи в нижнем регистре, разделитель — дефис
// (пробелы в маркере нормализуем к дефису при поиске: «pre chorus» → «pre-chorus»).
const RU_SECTION = {
    verse: "Куплет",
    chorus: "Припев",
    "pre-chorus": "Пре-припев",
    bridge: "Бридж",
    intro: "Интро",
    outro: "Финал",
    hook: "Хук",
    refrain: "Рефрен",
    interlude: "Интерлюдия",
    solo: "Соло",
};
const MARKER_RE = /^\[.*\]$/;
/**
 * Локализовать содержимое одного маркера (без квадратных скобок).
 * Возвращает `null`, если маркер НЕизвестен/технический (`[End]`, `[male vocals]`,
 * `[Guitar Solo]`, …) — такой заголовок скрываем, чтобы не мусорить текст.
 */
function localizeMarker(inner, language) {
    const trimmed = inner.trim();
    // Отделяем базовое имя от завершающего номера: «Verse 2», «Chorus-1», «Куплет 3».
    const m = trimmed.match(/^(.*?)[\s-]*(\d+)?$/);
    const rawBase = (m?.[1] ?? trimmed).trim();
    const num = m?.[2];
    const key = rawBase.toLowerCase().replace(/\s+/g, "-");
    const ru = RU_SECTION[key];
    if (!ru) {
        // Неизвестный/технический маркер ([End] и пр.) → скрываем (нет заголовка).
        return null;
    }
    if (language === "en") {
        // EN: известную секцию показываем оригинальным английским именем (+ номер).
        return num ? `${rawBase} ${num}` : rawBase;
    }
    return num ? `${ru} ${num}` : ru;
}
/**
 * Тот же разбор, но ПЛОСКОЙ строкой: заголовок секции — отдельной строкой,
 * блоки разделены пустой строкой. Нужен там, где локализованный текст кладётся
 * в редактируемое поле (форма «Релиз трека»), а не рисуется сегментами.
 * Повторный прогон уже локализованного текста безопасен: маркеров в нём нет,
 * поэтому строка возвращается как есть.
 */
export function localizeLyricsToPlainText(raw, language, options = {}) {
    return localizeLyrics(raw, language, options)
        .map((segment) => segment.text)
        .join("\n\n")
        .trim();
}
export function localizeLyrics(raw, language, options = {}) {
    const lines = raw.split("\n");
    const segments = [];
    let buffer = [];
    const flush = () => {
        if (buffer.length === 0)
            return;
        // Убираем ведущие/замыкающие пустые строки блока, но сохраняем внутренние.
        const text = buffer.join("\n").replace(/^\n+|\n+$/g, "");
        if (text.trim().length > 0) {
            segments.push({ kind: "text", text });
        }
        buffer = [];
    };
    for (const line of lines) {
        if (MARKER_RE.test(line.trim())) {
            flush();
            const inner = line.trim().slice(1, -1);
            const heading = localizeMarker(inner, language);
            // null → неизвестный/технический маркер ([End] и пр.): по умолчанию
            // заголовок скрываем, с keepUnknownMarkers — оставляем строку как есть.
            if (heading !== null) {
                segments.push({ kind: "heading", text: heading });
            }
            else if (options.keepUnknownMarkers) {
                segments.push({ kind: "heading", text: line.trim() });
            }
        }
        else {
            buffer.push(line);
        }
    }
    flush();
    return segments;
}
